import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clearCalFlow } from '../utils/calFlow';
import {
  CheckIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import AumSavingsCard, { AUM_DEFAULT } from './AumSavingsCard';

const HUBSPOT_MEETING_BASE = 'https://meetings.hubspot.com/amorjaria/sharpemetrix';

function toAumRange(n) {
  if (n < 10_000_000) return 'Under $10M';
  if (n < 50_000_000) return '$10M – $50M';
  if (n < 100_000_000) return '$50M – $100M';
  if (n < 500_000_000) return '$100M – $500M';
  if (n < 1_000_000_000) return '$500M – $1B';
  return 'Over $1B';
}

function isHubSpotBookedEvent(event) {
  const data = event?.data;
  if (!data) return false;
  if (data.meetingBookSucceeded === true) return true;
  if (data.meetingBooked === true) return true;
  if (typeof data === 'string' && /meetingBookSucceeded|meetings:booked/i.test(data)) return true;
  return false;
}

export default function CalOnboarding() {
  const { user, activateTrial, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const [aum, setAum] = useState(AUM_DEFAULT);
  const [booked, setBooked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState('');

  const meetingSrc = useMemo(() => {
    const params = new URLSearchParams({ embed: 'true' });
    if (user?.email) params.set('email', user.email);
    return `${HUBSPOT_MEETING_BASE}?${params.toString()}`;
  }, [user?.email]);

  useEffect(() => {
    const onMessage = (event) => {
      const origin = event.origin || '';
      if (origin && !/hubspot/i.test(origin)) return;
      if (isHubSpotBookedEvent(event)) setBooked(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const unlockPlatform = async () => {
    if (unlocking || unlocked) return;
    setUnlocking(true);
    setError('');
    try {
      await activateTrial({
        aum_range: toAumRange(aum),
        aum_estimate: aum,
        referral_source: 'Discovery call',
        discovery_call: true,
      });
      await completeOnboarding();
      setUnlocked(true);
    } catch (err) {
      console.error(err);
      setError('Account setup hit a snag — you can still enter the platform.');
      setUnlocked(true);
    } finally {
      setUnlocking(false);
    }
  };

  useEffect(() => {
    if (booked) unlockPlatform();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booked]);

  const enterPlatform = () => {
    clearCalFlow();
    navigate('/models', { replace: true });
  };

  return (
    <div className="min-h-screen bg-bg-100 flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-bg-300">
        <img src="/logo_wordmark.svg" alt="SharpeMetrix" className="h-7" />
        {user?.email && (
          <p className="text-text-300 text-sm truncate max-w-[50%]">{user.email}</p>
        )}
      </div>

      <div className="flex-1 px-6 py-8 lg:py-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 max-w-2xl">
            <p className="text-primary-100 text-sm font-medium mb-1">Discovery call</p>
            <h1 className="text-3xl font-bold text-text-100">
              {booked ? "You're booked." : 'Book a time with the team'}
            </h1>
            <p className="mt-2 text-text-200">
              {booked
                ? "We'll send a calendar invite shortly. In the meantime, take a look around."
                : "Pick a slot that works — we'll walk through the models, the signals, and how advisors use SharpeMetrix."}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              {booked ? (
                <div className="rounded-2xl border border-bg-300 bg-bg-200 p-8 sm:p-10">
                  <div className="w-14 h-14 rounded-2xl bg-signal-positive/15 flex items-center justify-center mb-6">
                    <CheckIcon className="w-7 h-7 text-signal-positive" />
                  </div>
                  <h2 className="text-2xl font-bold text-text-100">See you on the call</h2>
                  <p className="mt-3 text-text-200 leading-relaxed">
                    Your account is ready. You can explore the full platform now — model portfolios,
                    Power List rankings, and research — so the conversation is more useful when we meet.
                  </p>

                  {error && (
                    <p className="mt-4 text-sm text-red-400 bg-red-400/10 rounded-lg px-4 py-3">{error}</p>
                  )}

                  <button
                    onClick={enterPlatform}
                    disabled={unlocking && !unlocked}
                    className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-primary-100 px-5 py-3 text-sm font-semibold text-white hover:bg-primary-200 transition-all disabled:opacity-60"
                  >
                    {unlocking && !unlocked ? (
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>Explore the platform <ArrowRightIcon className="w-4 h-4" /></>
                    )}
                  </button>
                </div>
              ) : (
                <div className="rounded-2xl border border-bg-300 bg-bg-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-bg-300 flex items-center gap-3">
                    <CalendarDaysIcon className="w-5 h-5 text-primary-100" />
                    <div>
                      <p className="text-sm font-semibold text-text-100">Amar Morjaria · SharpeMetrix</p>
                      <p className="text-xs text-text-300">20-minute discovery call</p>
                    </div>
                  </div>
                  <div className="meetings-iframe-container relative h-[780px] overflow-hidden bg-white">
                    <iframe
                      title="Book a SharpeMetrix discovery call"
                      src={meetingSrc}
                      scrolling="no"
                      className="absolute inset-0 h-full w-full border-0"
                    />
                  </div>
                  <div className="px-5 py-4 border-t border-bg-300">
                    <button
                      type="button"
                      onClick={() => setBooked(true)}
                      className="text-xs text-text-300 hover:text-text-100 transition-colors"
                    >
                      Already booked? Continue here →
                    </button>
                  </div>
                </div>
              )}
            </div>

            <aside className="lg:col-span-5">
              <AumSavingsCard aum={aum} onChange={setAum} />
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}
